import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Bell, Clock, Loader2, Video } from "lucide-react";

import { fetchEventBySlug } from "@/lib/api/events";
import {
  fetchEventFeedPage,
  type EventFeedRow,
  type FeedPageCursor,
  type ParticipantRow,
} from "@/lib/api/participants";
import { fetchFeedRecommendations } from "@/lib/api/recommendations";
import { recordFeedView } from "@/lib/api/pitch-views";
import {
  fetchIncomingLikesCount,
  fetchLikedIds,
  fetchMatches,
  insertLike,
} from "@/lib/api/likes";
import { fetchMyMeetupMatchScores } from "@/lib/api/match-scores";
import { clearParticipantSession, getParticipantSession } from "@/lib/participant-session";
import { formatCountdown, isRevealed, msUntilReveal } from "@/lib/reveal";

import { LinkButton } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Modal } from "@/components/ui/Modal";
import { VideoAvatar } from "@/components/ui/VideoAvatar";
import { EmptyState, InlineAlert } from "@/components/ui/States";
import { ParticipantShell } from "@/components/layout/ParticipantShell";
import { TelegramConnectBlock } from "@/components/participant/TelegramConnectBlock";
import { FeedCard } from "@/features/feed/FeedCard";

// Number of cards shown per batch. The user pulls the next batch
// manually via the "Show more" button — no live infinite scroll
// (TZ item 5: manual batches on demand, not one-by-one streaming).
const BATCH_SIZE = 5;

export function FeedPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const session = slug ? getParticipantSession(slug) : null;
  const [matched, setMatched] = useState<EventFeedRow | null>(null);
  const [firstLikeTooltip, setFirstLikeTooltip] = useState<{
    targetId: string;
    targetName: string;
  } | null>(null);
  const tooltipTimerRef = useRef<number | null>(null);

  // Cards the user "burned through" in the current batch — either liked
  // or skipped. Once the current batch reaches zero visible cards, the
  // feed shows the empty-between-batches state and waits for the user
  // to tap "Show more" (TZ: empty until More is tapped).
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!slug) return;
    // No localStorage session at all → never authorised for this event.
    if (!session) {
      navigate(`/e/${slug}`, { replace: true });
    }
  }, [slug, session, navigate]);

  // V2-12: record the first feed view so the push scheduler can
  // distinguish "registered but never opened" (E3) from "browsing
  // without a profile" (E4). RPC is idempotent — only the first
  // call lands.
  useEffect(() => {
    if (session?.participantId) {
      void recordFeedView(session.participantId);
    }
  }, [session?.participantId]);

  const { data: event } = useQuery({
    queryKey: ["event-slug", slug],
    queryFn: () => fetchEventBySlug(slug ?? ""),
    enabled: Boolean(slug),
  });

  const feedQuery = useInfiniteQuery({
    queryKey: ["event-feed-page", session?.participantId],
    enabled: Boolean(session?.participantId),
    retry: false,
    initialPageParam: null as FeedPageCursor | null,
    queryFn: ({ pageParam }) =>
      fetchEventFeedPage(session?.participantId ?? "", BATCH_SIZE, pageParam),
    getNextPageParam: (last) => last.nextCursor,
  });

  // Organizer recommendations (TZ block C). Live alongside the
  // algorithmic feed but rendered above it — they're a direct
  // "the organizer thinks you two should meet" signal that should
  // never be buried under match-score sorting.
  const { data: recommendations = [] } = useQuery({
    queryKey: ["feed-recommendations", session?.participantId],
    queryFn: () => fetchFeedRecommendations(session?.participantId ?? ""),
    enabled: Boolean(session?.participantId),
    // Recs refresh slowly compared to likes/scores; polling every
    // 60s catches new ones the organizer drops in mid-event without
    // burning RPC calls.
    refetchInterval: 60_000,
  });

  const recommendedIds = useMemo(
    () => new Set(recommendations.map((r) => r.id)),
    [recommendations],
  );

  // Combine recs + algorithmic rows. Algorithmic rows that are also
  // recommended get tagged is_recommended (and pulled to the top via
  // the prepend below) instead of appearing twice. The final list is
  // [self, ...recommended, ...algorithmic without dups].
  const participants = useMemo<EventFeedRow[]>(() => {
    const algorithmic = feedQuery.data?.pages.flatMap((p) => p.rows) ?? [];
    const self = algorithmic.find((r) => r.is_self) ?? null;
    const algorithmicOthers = algorithmic.filter(
      (r) => !r.is_self && !recommendedIds.has(r.id),
    );
    const recOrdered: EventFeedRow[] = recommendations.map((r) => {
      // Strip the recommended_at field — FeedCard only knows about
      // EventFeedRow.
      const { recommended_at: _omit, ...rest } = r;
      void _omit;
      return rest;
    });
    return self
      ? [self, ...recOrdered, ...algorithmicOthers]
      : [...recOrdered, ...algorithmicOthers];
  }, [feedQuery.data, recommendations, recommendedIds]);
  const isLoading = feedQuery.isLoading;
  const feedFetchFailed = feedQuery.isError;

  const { data: matchScores = [] } = useQuery({
    queryKey: ["match-scores", session?.participantId],
    queryFn: () => fetchMyMeetupMatchScores(session?.participantId ?? ""),
    enabled: Boolean(session?.participantId),
    refetchInterval: 30_000,
  });

  const { data: likedIds = new Set<string>() } = useQuery({
    queryKey: ["liked-ids", session?.eventId, session?.participantId],
    queryFn: () => fetchLikedIds(session?.eventId ?? "", session?.participantId ?? ""),
    enabled: Boolean(session?.eventId && session?.participantId),
  });

  const { data: matches = [] } = useQuery({
    queryKey: ["matches", session?.eventId, session?.participantId],
    queryFn: () => fetchMatches(session?.eventId ?? "", session?.participantId ?? ""),
    enabled: Boolean(session?.eventId && session?.participantId),
  });

  const { data: incomingLikes = 0 } = useQuery({
    queryKey: ["incoming-likes", session?.eventId, session?.participantId],
    queryFn: () =>
      fetchIncomingLikesCount(session?.eventId ?? "", session?.participantId ?? ""),
    enabled: Boolean(session?.eventId && session?.participantId),
    refetchInterval: 30_000,
  });

  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const revealed = event ? isRevealed(event, now) : false;
  const countdown = event ? formatCountdown(msUntilReveal(event, now)) : "--:--:--";

  // Previously we redirected to /wrap as soon as event_date had passed.
  // That cut people off from the feed for events they were curious about
  // afterwards — "who actually came to that meetup?". We now keep the
  // feed open indefinitely; the only thing we suppress past event date
  // is the Like button (matches are already finalised) and the reveal
  // countdown. /wrap remains reachable directly for the survey.
  const isEventEnded = Boolean(
    event && new Date(event.event_date).getTime() < Date.now(),
  );

  const likeMutation = useMutation({
    mutationFn: (toId: string) =>
      insertLike(session?.eventId ?? "", session?.participantId ?? "", toId),
    // Surface RPC errors instead of swallowing them — the previous flow
    // silently failed if the like RPC threw (e.g. unique-violation on
    // the dedup'd email enqueue), giving the participant zero feedback
    // about why nothing happened on click.
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Couldn't save the like.";
      console.error("insertLike failed:", err);
      window.alert(message);
    },
    onSuccess: (result, toId) => {
      queryClient.invalidateQueries({
        queryKey: ["liked-ids", session?.eventId, session?.participantId],
      });

      // V2 spec frame F1: a liked card stays visible in the feed,
      // now with the "Liked · waiting" state. The user expects to
      // see who they liked — pulling cards out the moment they tap
      // Like reads as "did the click work?" instead of confirmation.
      // Only an explicit Skip removes a card from the batch.

      const FIRST_LIKE_FLAG = "finally.meetup.has-liked";
      if (!localStorage.getItem(FIRST_LIKE_FLAG)) {
        localStorage.setItem(FIRST_LIKE_FLAG, "1");
        const target = participants.find((p) => p.id === toId);
        if (target) {
          setFirstLikeTooltip({ targetId: toId, targetName: target.name });
          if (tooltipTimerRef.current) window.clearTimeout(tooltipTimerRef.current);
          tooltipTimerRef.current = window.setTimeout(
            () => setFirstLikeTooltip(null),
            6000,
          );
        }
      }

      if (result.matched) {
        queryClient.invalidateQueries({
          queryKey: ["matches", session?.eventId, session?.participantId],
        });
        const other = participants.find((p) => p.id === toId) ?? null;
        if (revealed) setMatched(other);
      }
    },
  });

  const handleSkip = (id: string) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleShowMore = () => {
    if (feedQuery.hasNextPage && !feedQuery.isFetchingNextPage) {
      feedQuery.fetchNextPage();
    }
  };

  const me = useMemo(
    () => participants.find((p) => p.is_self) ?? null,
    [participants],
  );

  // Server-side guard rails (run after the feed query lands):
  //
  //   1. Stale session — the localStorage `participantId` doesn't match
  //      any row in the DB (deleted participant, wiped DB, wrong slug).
  //      `find_event_participants` raises "Participant not found", which
  //      bubbles up as React Query error. We clear the bad session and
  //      send the user back to the welcome page where they can re-join.
  //
  //   2. Registered but no profile yet — only this state still bounces
  //      to /profile (or /goals). The "no video" state is now allowed
  //      on the feed: TZ block A explicitly lifts the video gate; the
  //      participant gets a text card and a top banner inviting them
  //      to record. See PitchStatusBanner below for the messaging.
  useEffect(() => {
    if (!slug || !session) return;
    if (isLoading) return;
    if (feedFetchFailed) {
      clearParticipantSession(slug);
      navigate(`/e/${slug}`, { replace: true });
      return;
    }
    // V2-4 lurker mode (spec L1): a profile-incomplete participant is
    // allowed to browse the feed read-only. We no longer redirect
    // them to /goals — the FeedCard locks the Like button and the top
    // banner explains the contract. They appear nowhere in others'
    // feeds (is_profile_complete = false already excludes them from
    // find_event_participants for non-self viewers).
  }, [slug, session, isLoading, feedFetchFailed, me, navigate]);

  // Three participant states feed-side (TZ block A item 4):
  //   * no profile  -> already redirected above to /goals (observer
  //                    state is a transition, not a steady state in
  //                    this build — we still require an email-OTP join)
  //   * profile, no video           -> can like, shown as text card
  //   * profile, video (any status) -> can like, shown as video card
  // A pitch in 'submitted' state (auto-approved at upload, awaiting
  // organizer-side moderation) doesn't gate likes — the participant
  // must be able to like back symmetrically.
  const canLike = Boolean(me?.is_profile_complete) && !isEventEnded;

  // Fresh score overlay — the find_event_participants RPC ships a
  // (score, match_reason) snapshot from the join, but score-meetup-
  // matches refreshes scores asynchronously. We pick the freshest of
  // the two so the user sees updated reasons without a feed refetch.
  const freshScoresById = useMemo(
    () => new Map(matchScores.map((s) => [s.to_participant_id, s])),
    [matchScores],
  );

  // What's actually visible right now: everyone the server gave us so
  // far, minus self, minus already-liked, minus dismissed-in-this-
  // session. Ordering is server-side (score desc nulls last, has_video
  // desc) so we no longer sort client-side; the only client transform
  // is the burn-through filter.
  const visibleFeed = useMemo(
    () =>
      participants.filter(
        (p) => !p.is_self && !dismissedIds.has(p.id),
      ),
    [participants, dismissedIds],
  );

  if (!session) return null;

  const totalLoaded = participants.filter((p) => !p.is_self).length;
  const visibleCount = visibleFeed.length;
  const hasNextBatch = feedQuery.hasNextPage ?? false;
  const counter =
    totalLoaded === 1 ? "1 match loaded" : `${totalLoaded} matches loaded`;

  return (
    <ParticipantShell matchesCount={matches.length}>
      <div className="flex flex-col items-center">
        {/* Past-event banner. Browsing stays open so people can revisit
            who was at a meetup they've been to, but we make it visually
            clear that likes are off and matches are final. A short link
            to /wrap (the survey) is preserved here for first-time post-
            event visitors who haven't filled it in yet. */}
        {isEventEnded ? (
          <div className="mb-5 w-full max-w-full sm:max-w-[420px]">
            <Card tone="default" padding="md">
              <div className="flex items-start gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#f1f5f9] text-[#475569]">
                  <Clock className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-bold text-[#1a1a1a]">
                    This event has ended
                  </p>
                  <p className="mt-0.5 text-[12px] text-[#6c757d]">
                    Likes are closed and matches are finalised. You can still
                    browse who was here.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(`/e/${slug}/wrap`)}
                    className="mt-2 text-[12px] font-semibold text-[#2563eb] hover:underline"
                  >
                    Leave feedback →
                  </button>
                </div>
              </div>
            </Card>
          </div>
        ) : null}

        {/* Status banners. Shown for any non-approved state — including
            'submitted' (under-review banner) — so the participant knows
            their pitch is still being moderated even while they can
            already like other people in the feed. The "no video yet"
            banner sits at the top of the no-video state — the new
            text-card UX from TZ block A: the user is fully in the
            feed, but their own card is a text-only card and they get
            a constant nudge to upgrade it. */}
          {me && !me.is_profile_complete ? (
            <div className="mb-5 w-full max-w-full sm:max-w-[420px]">
              <LurkerBanner slug={slug ?? ""} />
            </div>
          ) : me && !me.video_storage_path ? (
            <div className="mb-5 w-full max-w-full sm:max-w-[420px]">
              <NoVideoYetBanner slug={slug ?? ""} />
            </div>
          ) : me && me.pitch_status !== "approved" ? (
            <div className="mb-5 w-full max-w-full sm:max-w-[420px]">
              <PitchStatusBanner status={me.pitch_status} slug={slug ?? ""} />
            </div>
          ) : null}
          {/* TZ block E item 10: surface one-tap Telegram connect
              right inside the event so the participant doesn't have
              to dig into settings. The block hides itself when the
              user has already linked the bot. */}
          {session ? (
            <div className="mb-5 w-full max-w-full sm:max-w-[420px]">
              <TelegramConnectBlock participantId={session.participantId} />
            </div>
          ) : null}
          {!revealed && incomingLikes > 0 ? (
            <div className="mb-5 w-full max-w-full sm:max-w-[420px]">
              <Card tone="info" padding="md">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-[#2563eb]">
                    <Bell className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-[14px] font-bold text-[#1d4ed8]">
                      {incomingLikes} {incomingLikes === 1 ? "person" : "people"} liked you
                    </p>
                    <p className="text-[12px] text-[#1d4ed8]">
                      Names open in <strong>{countdown}</strong>
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          ) : null}

          {/* Feed header — counter is the total loaded count across all
              batches the user has pulled in. Width matches the single-
              column card stack below. */}
          <div className="mb-4 flex w-full max-w-full items-baseline justify-between sm:max-w-[480px]">
            <h1 className="text-[15px] font-bold tracking-tight text-[#1a1a1a]">
              Matched for you
            </h1>
            <span className="text-[13px] font-medium text-[#94a3b8]">
              {totalLoaded === 0 ? "0 matches" : counter}
            </span>
          </div>

          {/* Cards — single-column stack, capped at 480px so the hero
              circle stays comfortable on wide screens. Each card grows to
              its own content height (no inner scroll); the page-level
              scroll handles long feeds. */}
          <div className="w-full max-w-full sm:max-w-[480px]">
            {isLoading ? (
              <div className="grid place-items-center py-16">
                <Card padding="lg">
                  <p className="text-[13px] text-[#475569]">Loading participants…</p>
                </Card>
              </div>
            ) : totalLoaded === 0 ? (
              <div className="grid place-items-center px-4 py-16">
                <Card padding="lg">
                  <EmptyState
                    icon="👀"
                    title="Nothing in your feed yet"
                    description="As people complete their profiles and record pitches, they'll show up here ranked by relevance."
                  />
                </Card>
              </div>
            ) : visibleCount === 0 ? (
              // Batch consumed — TZ: the feed stays empty until the
              // participant taps Show more, no live tail. When no
              // more batches exist, we show a terminal end state.
              <div className="grid place-items-center px-4 py-16">
                <Card padding="lg">
                  {hasNextBatch ? (
                    <EmptyState
                      icon="✨"
                      title="You're through this batch"
                      description="Tap Show more to pull the next 5 recommendations."
                    />
                  ) : (
                    <EmptyState
                      icon="🏁"
                      title="That's everyone for now"
                      description="New people will be added as they record pitches. Check back closer to the event."
                    />
                  )}
                </Card>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {visibleFeed.map((p) => (
                  <FeedCard
                    key={p.id}
                    participant={p}
                    matchReason={
                      freshScoresById.get(p.id)?.reason ?? p.match_reason
                    }
                    isRecommended={recommendedIds.has(p.id)}
                    liked={likedIds.has(p.id)}
                    pending={
                      likeMutation.isPending && likeMutation.variables === p.id
                    }
                    disabled={!canLike}
                    tooltip={
                      firstLikeTooltip?.targetId === p.id
                        ? firstLikeTooltip.targetName
                        : null
                    }
                    onLike={() => canLike && likeMutation.mutate(p.id)}
                    onSkip={() => handleSkip(p.id)}
                    viewerEventId={session.eventId}
                    viewerParticipantId={session.participantId}
                  />
                ))}
              </div>
            )}

            {/* Show more — visible whenever there's a next batch the
                server can serve. Sits below the card stack on a full
                batch; sits inside the empty-batch card above when the
                stack is empty. Disabled while the next page is in
                flight. */}
            {visibleCount > 0 && hasNextBatch ? (
              <div className="mt-5 grid place-items-center">
                <button
                  type="button"
                  onClick={handleShowMore}
                  disabled={feedQuery.isFetchingNextPage}
                  className="inline-flex items-center gap-2 rounded-[12px] border border-[#e2e8f0] bg-white px-5 py-3 text-[14px] font-semibold text-[#1a1a1a] shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition hover:bg-[#f8fafc] disabled:opacity-60"
                >
                  {feedQuery.isFetchingNextPage ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    <>Show more (next {BATCH_SIZE})</>
                  )}
                </button>
              </div>
            ) : null}
            {visibleCount === 0 && hasNextBatch ? (
              <div className="mt-5 grid place-items-center">
                <button
                  type="button"
                  onClick={handleShowMore}
                  disabled={feedQuery.isFetchingNextPage}
                  className="inline-flex items-center gap-2 rounded-[12px] bg-[#2563eb] px-5 py-3 text-[14px] font-bold text-white shadow-[0_3px_12px_rgba(37,99,235,0.28)] transition hover:bg-[#1d4ed8] disabled:opacity-60"
                >
                  {feedQuery.isFetchingNextPage ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    <>Show more</>
                  )}
                </button>
              </div>
            ) : null}
        </div>
      </div>

      <Modal open={Boolean(matched)} onClose={() => setMatched(null)} maxWidth={400}>
        {matched ? (
          <MatchModalContent
            viewerEventId={session.eventId}
            viewerParticipantId={session.participantId}
            participant={matched}
          />
        ) : null}
      </Modal>
    </ParticipantShell>
  );
}

function LurkerBanner({ slug }: { slug: string }) {
  return (
    <Card tone="info" padding="md">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white text-[#2563eb]">
          <Video className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-bold text-[#1d4ed8]">
            You're just browsing
          </p>
          <p className="mt-0.5 text-[12px] text-[#1e3a8a]">
            Set up a profile to like people and get matched.
          </p>
          <LinkButton to={`/e/${slug}/goals`} size="sm" className="mt-2.5">
            Set up profile
          </LinkButton>
        </div>
      </div>
    </Card>
  );
}

function NoVideoYetBanner({ slug }: { slug: string }) {
  return (
    <Card tone="default" padding="md">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#fef3c7] text-[#92400e]">
          <Video className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-bold text-[#1a1a1a]">
            Your card is text-only
          </p>
          <p className="mt-0.5 text-[12px] text-[#6c757d]">
            Record a 60-second pitch to get a live video card — and unlock an
            instant Adizes read of how your delivery lands.
          </p>
          <LinkButton to={`/e/${slug}/pitch`} size="sm" className="mt-2.5">
            Record pitch
          </LinkButton>
        </div>
      </div>
    </Card>
  );
}

function PitchStatusBanner({
  status,
  slug,
}: {
  status: ParticipantRow["pitch_status"];
  slug: string;
}) {
  if (status === "submitted") {
    return (
      <InlineAlert tone="warning">
        <strong className="block">Pitch received — under review.</strong>
        Usually live within 2 hours. We'll email you once approved.
      </InlineAlert>
    );
  }
  if (status === "flagged") {
    return (
      <Card tone="flagged" padding="md">
        <p className="text-[13px] font-bold text-[#92400e]">Your pitch needs a re-record</p>
        <p className="mt-1 text-[13px] text-[#78350f]">
          The organizer flagged your pitch for a quick fix. Re-record below to get back into the feed.
        </p>
        <LinkButton to={`/e/${slug}/pitch`} size="sm" className="mt-3">
          Re-record
        </LinkButton>
      </Card>
    );
  }
  if (status === "rejected") {
    return (
      <Card tone="danger" padding="md">
        <p className="text-[13px] font-bold text-[#991b1b]">Your pitch wasn't approved</p>
        <p className="mt-1 text-[13px] text-[#991b1b]">
          Record a new pitch to get back into the feed.
        </p>
        <LinkButton to={`/e/${slug}/pitch`} size="sm" className="mt-3" variant="destructive">
          Record new pitch
        </LinkButton>
      </Card>
    );
  }
  return (
    <Card tone="flagged" padding="md">
      <p className="text-[13px] font-bold text-[#92400e]">You're in browse mode</p>
      <p className="mt-1 text-[13px] text-[#78350f]">
        Without a pitch you won't appear in feeds and won't get matches.
      </p>
      <LinkButton to={`/e/${slug}/pitch`} size="sm" className="mt-3">
        Record Pitch Now
      </LinkButton>
    </Card>
  );
}

function MatchModalContent({
  viewerEventId,
  viewerParticipantId,
  participant,
}: {
  viewerEventId: string;
  viewerParticipantId: string;
  participant: EventFeedRow;
}) {
  const roleLine = [participant.role, participant.company].filter(Boolean).join(" · ");
  return (
    <div className="text-center">
      <div className="text-5xl">🎉</div>
      <h2 className="mt-3 text-[22px] font-bold tracking-tight text-[#1a1a1a]">It's a match!</h2>
      <p className="mt-1 text-[13px] text-[#6c757d]">
        {participant.name} also wants to meet you.
      </p>

      <div className="mx-auto mt-5 inline-block">
        <VideoAvatar
          name={participant.name}
          size={96}
          bordered
          videoStoragePath={participant.video_storage_path}
          eventId={viewerEventId}
          viewerParticipantId={viewerParticipantId}
        />
      </div>
      <p className="mt-3 text-[18px] font-bold text-[#1a1a1a]">{participant.name}</p>
      {roleLine ? <p className="text-[13px] text-[#6c757d]">{roleLine}</p> : null}

      <p className="mt-4 text-[13px] text-[#6c757d]">
        Open the Matches tab to see contact details and message them.
      </p>
    </div>
  );
}
